import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../../style/attendanceContent.css";
import { PageHeader } from "../../components/admin/AdminUI";
import { formatDmy } from "../../utils/dateFormat";

const CLASS_OPTIONS = ["OT", "NT", "BD", "BLC", "BCMF", "MASTERCLASS", "HH", "CBH", "CBD", "BWG", "MMM", "CONFERENCE"];

const swalError = (text) =>
  Swal.fire({ text, icon: "error", background: "white", color: "black", confirmButtonColor: "#0d9488" });
const swalSuccess = (text) =>
  Swal.fire({ text, icon: "success", background: "white", color: "black", confirmButtonColor: "#0d9488" });

// Classroom mengirim deadline dan waktu turn-in dalam UTC, sedangkan yang
// dibaca admin adalah jam Jakarta — zona waktunya dipaksa eksplisit, bukan
// mengikuti jam browser, supaya penilaian dari laptop yang sedang di zona lain
// tidak menggeser tampilan deadline.
const fmtWIB = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const time = new Intl.DateTimeFormat("id-ID", {
    timeZone: "Asia/Jakarta",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
  return `${formatDmy(d)} ${time} WIB`;
};

// "missing" dan "pending" sama-sama berarti belum ada turn-in; yang membedakan
// hanya apakah deadline sudah lewat, dan itulah yang menentukan warnanya.
// Google Classroom punya EMPAT tipe lampiran, dan bentuknya berbeda-beda:
//   driveFile    { id, title, alternateLink, thumbnailUrl }
//   youTubeVideo { id, title, alternateLink, thumbnailUrl }
//   link         { url, title, thumbnailUrl }          <- TANPA id/alternateLink
//   form         { formUrl, responseUrl, title, thumbnailUrl }
//
// Sebelumnya kode memakai `a.driveFile?.title || a.link` — untuk lampiran
// bertipe `link`, ekspresi itu menghasilkan OBJEK `{url,title,thumbnailUrl}`
// yang lalu dirender sebagai anak React. React melemparkan "Objects are not
// valid as a React child", seluruh tree ter-unmount, dan halaman jadi PUTIH
// TOTAL. Kelas HH batch 1 punya 3 lampiran Dropbox semacam itu; CBH batch 1
// seluruhnya driveFile, jadi kelihatan normal — itu sebabnya bug ini hanya
// muncul di sebagian kelas.
//
// Semua tipe diratakan ke satu bentuk di sini supaya tidak ada lagi tempat
// yang perlu tahu bedanya.
const normalizeAttachment = (a) => {
  if (a && a.driveFile) {
    return {
      title: a.driveFile.title || "(tanpa nama)",
      externalLink: a.driveFile.alternateLink || null,
      driveFileId: a.driveFile.id || null,
      kind: "drive",
    };
  }
  if (a && a.link) {
    return {
      title: a.link.title || a.link.url || "(tautan)",
      externalLink: a.link.url || null,
      driveFileId: null,
      kind: "link",
    };
  }
  if (a && a.youTubeVideo) {
    return {
      title: a.youTubeVideo.title || "(video YouTube)",
      externalLink: a.youTubeVideo.alternateLink || null,
      driveFileId: null,
      kind: "youtube",
    };
  }
  if (a && a.form) {
    return {
      title: a.form.title || "(Google Form)",
      externalLink: a.form.formUrl || a.form.responseUrl || null,
      driveFileId: null,
      kind: "form",
    };
  }
  // Lampiran yang seluruh isinya null memang ada di data nyata (1 di HH) —
  // biasanya file yang sudah dihapus student dari Drive.
  return {
    title: "(file tidak tersedia — kemungkinan sudah dihapus student)",
    externalLink: null,
    driveFileId: null,
    kind: "unknown",
  };
};

// Batas lampiran Create assignment — sama dengan batas di server
// (createCoursework dan fileUpload di index.js).
const MAX_FILES = 10;
const MAX_FILE_BYTES = 15 * 1024 * 1024;

const fmtFileSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const fileIconClass = (file) => {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (file.type === "application/pdf" || ext === "pdf") return "bi-file-earmark-pdf";
  if (["doc", "docx", "odt", "rtf"].includes(ext)) return "bi-file-earmark-word";
  if (["xls", "xlsx", "csv", "ods"].includes(ext)) return "bi-file-earmark-excel";
  if (["ppt", "pptx", "odp"].includes(ext)) return "bi-file-earmark-ppt";
  if (file.type.startsWith("video/")) return "bi-file-earmark-play";
  if (file.type.startsWith("audio/")) return "bi-file-earmark-music";
  if (["zip", "rar", "7z"].includes(ext)) return "bi-file-earmark-zip";
  return "bi-file-earmark-text";
};

// Satu baris di daftar lampiran form Create/Edit. Dibangun lewat DOM (bukan
// innerHTML) karena nama file berasal dari komputer pengguna atau dari
// Classroom dan bisa berisi karakter HTML.
//   thumbSrc  gambar pratinjau (file gambar lokal); kalau kosong dipakai ikon
//   href      dibuka di tab baru saat nama diklik
//   actions   [{ label, title, onClick, icon }] — icon: tombol "×" polos
const fileRow = ({ thumbSrc, iconClass, name, href, sub, subColor = "#888", muted = false, actions = [] }) => {
  const row = document.createElement("div");
  row.style.cssText = `display:flex;align-items:center;gap:10px;padding:6px 8px;margin-bottom:6px;border:1px solid #eee;border-radius:8px;${muted ? "opacity:.55" : ""}`;

  const thumb = document.createElement("div");
  thumb.style.cssText =
    "width:44px;height:44px;flex:none;border-radius:6px;background:#f4f4f4;display:flex;align-items:center;justify-content:center;overflow:hidden";
  if (thumbSrc) {
    const img = document.createElement("img");
    img.src = thumbSrc;
    img.alt = "";
    img.style.cssText = "width:100%;height:100%;object-fit:cover";
    thumb.append(img);
  } else {
    const icon = document.createElement("i");
    icon.className = `bi ${iconClass}`;
    icon.style.cssText = "font-size:22px;color:#0d9488";
    thumb.append(icon);
  }

  const info = document.createElement("div");
  info.style.cssText = "min-width:0;flex:1";
  const label = document.createElement(href ? "a" : "span");
  if (href) {
    label.href = href;
    label.target = "_blank";
    label.rel = "noreferrer";
    label.title = `${name} — click to preview`;
  }
  label.textContent = name;
  label.style.cssText = `display:block;color:#0d9488;text-decoration:${muted ? "line-through" : "none"};overflow:hidden;text-overflow:ellipsis;white-space:nowrap`;
  info.append(label);
  if (sub) {
    const s = document.createElement("div");
    s.textContent = sub;
    s.style.cssText = `font-size:11px;color:${subColor};overflow:hidden;text-overflow:ellipsis;white-space:nowrap`;
    info.append(s);
  }

  row.append(thumb, info);
  actions.forEach(({ label: text, title, onClick, icon }) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = text;
    b.title = title || text;
    b.className = icon ? "" : "btn btn-sm btn-outline-secondary";
    if (icon) b.style.cssText = "background:none;border:none;font-size:20px;line-height:1;color:#888;cursor:pointer";
    else b.style.cssText = "white-space:nowrap";
    b.addEventListener("click", onClick);
    row.append(b);
  });
  return row;
};

// Baris untuk file lokal yang baru dipilih (pratinjau gambar/ikon + ukuran).
const localFileRow = (file, url, extra = {}) =>
  fileRow({
    thumbSrc: file.type.startsWith("image/") ? url : null,
    iconClass: fileIconClass(file),
    name: file.name,
    href: url,
    sub: `${fmtFileSize(file.size)}${file.size > MAX_FILE_BYTES ? " — too large (max 15 MB)" : ""}`,
    subColor: file.size > MAX_FILE_BYTES ? "#c0392b" : "#888",
    ...extra,
  });

// Lampiran yang sudah ada di tugas Classroom, dalam bentuk yang bisa ditampilkan.
const materialInfo = (m) => {
  if (m.driveFile) {
    const f = m.driveFile.driveFile || {};
    return { name: f.title || "(Drive file)", href: f.alternateLink, iconClass: fileIconClass({ name: f.title || "", type: "" }), drive: true };
  }
  if (m.youtubeVideo) return { name: m.youtubeVideo.title || "(YouTube video)", href: m.youtubeVideo.alternateLink, iconClass: "bi-youtube" };
  if (m.link) return { name: m.link.title || m.link.url, href: m.link.url, iconClass: "bi-link-45deg" };
  if (m.form) return { name: m.form.title || "(Google Form)", href: m.form.formUrl, iconClass: "bi-ui-checks" };
  return { name: "(attachment)", href: null, iconClass: "bi-file-earmark" };
};

// Pilihan "Post: Now / Schedule". Hanya untuk tugas yang belum terbit —
// Classroom tidak bisa mengembalikan tugas yang sudah terbit menjadi draft.
const scheduleHtml = `
    <div class="row g-2 mb-2">
      <div class="col-4">
        <label for="cw-post" style="color:#888;font-size:12px">Post</label>
        <select id="cw-post" class="form-select form-select-sm">
          <option value="now">Now</option>
          <option value="schedule">Schedule</option>
        </select>
      </div>
      <div class="col-5 cw-sched" style="display:none">
        <label for="cw-sched-date" style="color:#888;font-size:12px">Post date</label>
        <input id="cw-sched-date" type="date" class="form-control form-control-sm" />
      </div>
      <div class="col-3 cw-sched" style="display:none">
        <label for="cw-sched-time" style="color:#888;font-size:12px">Time (WIB)</label>
        <input id="cw-sched-time" type="time" class="form-control form-control-sm" value="07:00" />
      </div>
    </div>`;

// Menampilkan kolom tanggal/jam posting hanya saat "Schedule" dipilih, dan
// menyesuaikan catatan di bawah form.
const wireSchedule = (notes) => {
  const select = document.getElementById("cw-post");
  if (!select) return;
  const sync = () => {
    const scheduled = select.value === "schedule";
    document.querySelectorAll(".cw-sched").forEach((el) => {
      el.style.display = scheduled ? "" : "none";
    });
    const note = document.getElementById("cw-note");
    if (note) note.textContent = scheduled ? notes.schedule : notes.now;
  };
  select.addEventListener("change", sync);
  sync();
};

// Form Create/Edit assignment (isi Swal). `files` hanya dipakai Create —
// lampiran tidak bisa diubah setelah tugas dibuat.
const assignmentFormHtml = ({ files = "", note, schedule = false }) => `
  <div style="text-align:left;font-size:13px">
    <label for="cw-title" style="color:#888;font-size:12px">Title</label>
    <input id="cw-title" class="form-control form-control-sm mb-2" maxlength="200" />
    <label for="cw-desc" style="color:#888;font-size:12px">Instructions (optional)</label>
    <textarea id="cw-desc" class="form-control form-control-sm mb-2" rows="4"></textarea>
    ${files}
    <div class="row g-2">
      <div class="col-4">
        <label for="cw-points" style="color:#888;font-size:12px">Max points</label>
        <input id="cw-points" type="number" min="1" class="form-control form-control-sm" value="100" />
      </div>
      <div class="col-5">
        <label for="cw-date" style="color:#888;font-size:12px">Deadline (optional)</label>
        <input id="cw-date" type="date" class="form-control form-control-sm" />
      </div>
      <div class="col-3">
        <label for="cw-time" style="color:#888;font-size:12px">Time (WIB)</label>
        <input id="cw-time" type="time" class="form-control form-control-sm" value="23:59" />
      </div>
    </div>
    ${schedule ? scheduleHtml : ""}
    <p id="cw-note" style="margin:12px 0 0;color:#888;font-size:12px">${note}</p>
  </div>`;

// Deadline diisi dalam jam Jakarta dan dikirim sebagai satu titik waktu;
// server yang memecahnya ke tanggal + jam UTC milik Classroom.
const readAssignmentForm = ({ checkDeadline = true } = {}) => {
  const title = document.getElementById("cw-title").value.trim();
  const description = document.getElementById("cw-desc").value.trim();
  const maxPoints = Number(document.getElementById("cw-points").value);
  const date = document.getElementById("cw-date").value;
  const time = document.getElementById("cw-time").value || "23:59";
  if (!title) return { error: "Title is required." };
  if (!Number.isFinite(maxPoints) || maxPoints <= 0) return { error: "Max points must be greater than 0." };
  let dueAt = null;
  if (date) {
    const due = new Date(`${date}T${time}:00+07:00`);
    if (Number.isNaN(due.getTime())) return { error: "Invalid deadline." };
    if (checkDeadline && due.getTime() <= Date.now()) return { error: "Deadline must be in the future." };
    dueAt = due.toISOString();
  }
  // scheduledAt: undefined = form tanpa pilihan Post, null = posting sekarang.
  let scheduledAt;
  const post = document.getElementById("cw-post");
  if (post) {
    scheduledAt = null;
    if (post.value === "schedule") {
      const sDate = document.getElementById("cw-sched-date").value;
      const sTime = document.getElementById("cw-sched-time").value || "07:00";
      if (!sDate) return { error: "Choose the post date." };
      const at = new Date(`${sDate}T${sTime}:00+07:00`);
      if (Number.isNaN(at.getTime())) return { error: "Invalid post date." };
      if (at.getTime() <= Date.now() + 60 * 1000) return { error: "Post time must be in the future." };
      if (dueAt && new Date(dueAt).getTime() <= at.getTime()) return { error: "Deadline must be after the post time." };
      scheduledAt = at.toISOString();
    }
  }
  return { title, description, maxPoints, dueAt, scheduledAt };
};

// Sama dengan dueAtOf di ClassroomController: dueDate/dueTime Classroom dalam
// UTC, dueTime menghilangkan field yang bernilai 0.
const dueAtOfCourseWork = (cw) => {
  const d = cw.dueDate;
  if (!d || !d.year || !d.month || !d.day) return null;
  const t = cw.dueTime || {};
  return new Date(Date.UTC(d.year, d.month - 1, d.day, t.hours || 0, t.minutes || 0)).toISOString();
};

// ISO -> nilai input date/time dalam jam Jakarta (UTC+7, tanpa DST).
const wibParts = (iso) => {
  if (!iso) return { date: "", time: "" };
  const wib = new Date(new Date(iso).getTime() + 7 * 60 * 60 * 1000).toISOString();
  return { date: wib.slice(0, 10), time: wib.slice(11, 16) };
};

const LATE_BADGES = {
  late: { label: "Terlambat", color: "#c0392b", background: "#fdecea" },
  on_time: { label: "Tepat waktu", color: "#2e7d5b", background: "#e8f5f0" },
  missing: { label: "Belum submit", color: "#c0392b", background: "#fdecea" },
  pending: { label: "Belum submit", color: "#8a6d1f", background: "#fdf6e3" },
  no_deadline: { label: "Tanpa deadline", color: "#777", background: "#f1f1f1" },
};

const AssignmentGradingPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const address = useLocation();
  const [errorMe, setErrorMe] = useState(false);

  const [classCode, setClassCode] = useState("OT");
  const [batch, setBatch] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [linking, setLinking] = useState(false);
  const [links, setLinks] = useState([]);

  const [coursework, setCoursework] = useState([]);
  const [loadingCoursework, setLoadingCoursework] = useState(false);

  const [activeCourseWork, setActiveCourseWork] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [dueAt, setDueAt] = useState(null);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [scoreInputs, setScoreInputs] = useState({});
  const [studentSearch, setStudentSearch] = useState("");
  // Index di dalam flatFiles; null = popup tertutup.
  const [viewerIndex, setViewerIndex] = useState(null);
  const [viewerMsg, setViewerMsg] = useState("");

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch (error) {
      setErrorMe(true);
    }
  };

  const fetchCourses = async () => {
    try {
      const res = await axios.get("/classroom/courses");
      setCourses(res.data.courses || []);
    } catch (error) {
      // Most likely REFRESH_TOKEN doesn't have Classroom scope yet — surfaced
      // via the "Link Course" button instead of a page-load popup.
    }
  };

  // Baca tabel lokal, tidak lewat Google — jadi daftar ini tetap muncul walau
  // dropdown Course di atasnya kosong karena scope OAuth belum lengkap.
  const fetchLinks = async () => {
    try {
      const res = await axios.get("/classroom/links");
      setLinks(res.data || []);
    } catch (error) {
      setLinks([]);
    }
  };

  // Mengembalikan daftarnya, bukan hanya menyimpan ke state: tombol "View
  // Submissions" perlu tahu isinya SEKARANG untuk langsung membuka submission
  // pertama, dan state React belum terbaca di tick yang sama.
  const fetchCoursework = async () => {
    if (!classCode || !batch) return [];
    setLoadingCoursework(true);
    try {
      const res = await axios.get(`/classroom/${classCode}/${batch}/coursework`);
      const list = res.data.courseWork || [];
      setCoursework(list);
      return list;
    } catch (error) {
      setCoursework([]);
      return [];
    } finally {
      setLoadingCoursework(false);
    }
  };

  useEffect(() => {
    setLocationAdmin(address);
    getMe();
    fetchCourses();
    fetchLinks();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account !",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  // autoOpenRef dipakai sebagai ref, bukan state: nilainya cuma penanda sekali
  // pakai untuk efek di bawah, dan menyimpannya di state akan memicu render
  // tambahan sekaligus menjalankan ulang efek ini.
  const autoOpenRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setActiveCourseWork(null);
    setSubmissions([]);
    setDueAt(null);
    setStudentSearch("");
    (async () => {
      const list = await fetchCoursework();
      // Kelas/batch bisa sudah berganti lagi selama request berjalan — tanpa
      // penjaga ini, respons yang datang terlambat akan membuka submission
      // milik kelas yang sudah ditinggalkan.
      if (cancelled) return;
      if (autoOpenRef.current && list.length > 0) {
        autoOpenRef.current = false;
        openSubmissions(list[0]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classCode, batch]);

  const handleLink = async () => {
    if (!batch || !selectedCourseId) return swalError("Batch and course are required.");
    const course = courses.find((c) => c.id === selectedCourseId);
    setLinking(true);
    try {
      await axios.post("/classroom/link", { classCode, batch, courseId: selectedCourseId, courseName: course?.name });
      swalSuccess("Classroom linked.");
      fetchCoursework();
      fetchLinks();
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to link Classroom. This usually means REFRESH_TOKEN doesn't have Classroom scope yet.");
    } finally {
      setLinking(false);
    }
  };

  // Tugas dibuat lewat API supaya tercatat milik project kita di Google —
  // hanya tugas seperti itu yang nilainya boleh ditulis dari halaman ini.
  // Deadline diisi dalam jam Jakarta dan dikirim sebagai satu titik waktu;
  // server yang memecahnya ke tanggal + jam UTC milik Classroom.
  const handleCreateAssignment = async () => {
    // File yang dipilih dikumpulkan di sini, bukan dibaca dari <input>:
    // memilih lagi menambah ke daftar (input file biasa justru menggantinya),
    // dan tiap file bisa dibuang satu per satu dari pratinjau.
    let picked = [];
    const previewUrls = new Map();
    const urlOf = (file) => {
      if (!previewUrls.has(file)) previewUrls.set(file, URL.createObjectURL(file));
      return previewUrls.get(file);
    };

    // Nama file diklik membuka file di tab baru: PDF, gambar, video, dan teks
    // tampil langsung di browser; tipe lain akan terunduh.
    const renderFiles = () => {
      const list = document.getElementById("cw-file-list");
      if (!list) return;
      list.replaceChildren(
        ...picked.map((file, i) =>
          localFileRow(file, urlOf(file), {
            actions: [
              {
                label: "×",
                title: "Remove",
                icon: true,
                onClick: () => {
                  picked = picked.filter((_, j) => j !== i);
                  renderFiles();
                },
              },
            ],
          }),
        ),
      );
    };

    const result = await Swal.fire({
      title: "Create assignment",
      html: assignmentFormHtml({
        files: `
          <div style="color:#888;font-size:12px">Files for all students (optional, up to ${MAX_FILES} files, max 15 MB each)</div>
          <input id="cw-files" type="file" multiple style="display:none" />
          <div id="cw-file-list" class="mb-1"></div>
          <button id="cw-add-files" type="button" class="btn btn-sm btn-outline-secondary mb-2">
            <i class="bi bi-paperclip"></i> Add files
          </button>`,
        note: "",
        schedule: true,
      }),
      background: "white",
      color: "black",
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonColor: "#0d9488",
      confirmButtonText: "Create & publish",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      didOpen: () => {
        wireSchedule({
          now: `The assignment is published to the Classroom course of ${classCode} batch ${batch} right away, and its students are notified by Google Classroom.`,
          schedule: `The assignment is saved as a draft in the Classroom course of ${classCode} batch ${batch}. Google Classroom publishes it and notifies students at the post time (usually within a few minutes after it).`,
        });
        const input = document.getElementById("cw-files");
        document.getElementById("cw-add-files").addEventListener("click", () => input.click());
        input.addEventListener("change", () => {
          picked = [...picked, ...Array.from(input.files || [])];
          // Dikosongkan supaya memilih file yang sama lagi tetap memicu change.
          input.value = "";
          renderFiles();
        });
      },
      didDestroy: () => previewUrls.forEach((url) => URL.revokeObjectURL(url)),
      preConfirm: async () => {
        const files = picked;
        if (files.length > MAX_FILES) return Swal.showValidationMessage(`At most ${MAX_FILES} files can be attached.`);
        const tooBig = files.find((f) => f.size > MAX_FILE_BYTES);
        if (tooBig) return Swal.showValidationMessage(`File too large (max 15 MB): ${tooBig.name}`);
        const fields = readAssignmentForm();
        if (fields.error) return Swal.showValidationMessage(fields.error);
        const { title, description, maxPoints, dueAt, scheduledAt } = fields;
        try {
          // FormData supaya file ikut terkirim.
          const body = new FormData();
          body.append("title", title);
          body.append("description", description);
          body.append("maxPoints", String(maxPoints));
          if (dueAt) body.append("dueAt", dueAt);
          if (scheduledAt) body.append("scheduledAt", scheduledAt);
          files.forEach((f) => body.append("files", f));
          const res = await axios.post(`/classroom/${classCode}/${batch}/coursework`, body);
          return res.data;
        } catch (error) {
          return Swal.showValidationMessage(error.response?.data?.msg || "Failed to create the assignment.");
        }
      },
    });
    if (!result.isConfirmed || !result.value) return;
    swalSuccess(result.value.msg);
    fetchCoursework();
  };

  // Hanya untuk tugas yang dibuat dari website, sama seperti hapus.
  //
  // Lampiran: Classroom API tidak mengizinkan daftar lampiran (materials)
  // diubah lewat patch. Karena itu:
  //  - "Replace" menimpa ISI file Drive yang sudah terlampir — lampirannya
  //    tetap sama, submission student aman;
  //  - menambah atau membuang lampiran membuat ulang tugasnya di Classroom,
  //    dan server hanya mengizinkannya selama belum ada student yang
  //    mengumpulkan.
  const handleEditAssignment = async (cw) => {
    const original = wibParts(dueAtOfCourseWork(cw));
    const isScheduled = cw.state === "DRAFT";
    const materials = cw.materials || [];
    const kept = materials.map(() => true);
    const replacements = new Map(); // index material -> File pengganti
    let picked = [];
    let replaceIndex = null;
    const previewUrls = new Map();
    const urlOf = (file) => {
      if (!previewUrls.has(file)) previewUrls.set(file, URL.createObjectURL(file));
      return previewUrls.get(file);
    };
    const structural = () => picked.length > 0 || kept.some((k) => !k);

    const render = () => {
      const existing = document.getElementById("cw-existing");
      const added = document.getElementById("cw-file-list");
      const warn = document.getElementById("cw-recreate-note");
      if (!existing || !added || !warn) return;

      existing.replaceChildren(
        ...(materials.length === 0
          ? [Object.assign(document.createElement("div"), { textContent: "No files attached.", style: "color:#aaa;font-size:12px;margin-bottom:6px" })]
          : materials.map((m, i) => {
              const info = materialInfo(m);
              if (!kept[i]) {
                return fileRow({
                  ...info,
                  sub: "Will be removed",
                  subColor: "#c0392b",
                  muted: true,
                  actions: [{ label: "Undo", onClick: () => { kept[i] = true; render(); } }],
                });
              }
              const replacement = replacements.get(i);
              const remove = { label: "×", title: "Remove", icon: true, onClick: () => { kept[i] = false; replacements.delete(i); render(); } };
              if (replacement) {
                return localFileRow(replacement, urlOf(replacement), {
                  sub: `${fmtFileSize(replacement.size)} · replaces ${info.name}`,
                  actions: [{ label: "Undo", onClick: () => { replacements.delete(i); render(); } }, remove],
                });
              }
              const actions = [];
              // Hanya file Drive yang isinya bisa ditimpa.
              if (info.drive) {
                actions.push({
                  label: "Replace",
                  title: "Replace this file's content",
                  onClick: () => {
                    replaceIndex = i;
                    document.getElementById("cw-replace-input").click();
                  },
                });
              }
              actions.push(remove);
              return fileRow({ ...info, sub: info.drive ? "Google Drive" : null, actions });
            })),
      );

      added.replaceChildren(
        ...picked.map((file, i) =>
          localFileRow(file, urlOf(file), {
            sub: `${fmtFileSize(file.size)} · new`,
            actions: [{ label: "×", title: "Remove", icon: true, onClick: () => { picked = picked.filter((_, j) => j !== i); render(); } }],
          }),
        ),
      );

      warn.style.display = structural() ? "block" : "none";
    };

    const result = await Swal.fire({
      title: "Edit assignment",
      html: assignmentFormHtml({
        files: `
          <div style="color:#888;font-size:12px">Files for all students</div>
          <div id="cw-existing"></div>
          <div id="cw-file-list"></div>
          <input id="cw-files" type="file" multiple style="display:none" />
          <input id="cw-replace-input" type="file" style="display:none" />
          <button id="cw-add-files" type="button" class="btn btn-sm btn-outline-secondary mb-2">
            <i class="bi bi-paperclip"></i> Add files
          </button>
          <div id="cw-recreate-note" style="display:none;background:#fdf6e3;color:#6b5516;border-radius:6px;padding:8px 10px;margin-bottom:8px;font-size:12px">
            Google Classroom does not allow adding or removing files on an existing assignment, so saving will
            <b>re-create this assignment</b> in Classroom and students are notified again. This is only possible
            while no student has turned it in yet. To keep submissions, use <b>Replace</b> instead.
          </div>`,
        note: "Changes are saved to Google Classroom right away.",
        // Tugas terjadwal (draft) masih bisa diganti jadwalnya atau langsung
        // diterbitkan; tugas yang sudah terbit tidak bisa dijadwalkan lagi.
        schedule: isScheduled,
      }),
      background: "white",
      color: "black",
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonColor: "#0d9488",
      confirmButtonText: "Save changes",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      // Diisi lewat .value, bukan disisipkan ke HTML: judul dan instruksi
      // berasal dari input admin.
      didOpen: () => {
        document.getElementById("cw-title").value = cw.title || "";
        document.getElementById("cw-desc").value = cw.description || "";
        document.getElementById("cw-points").value = cw.maxPoints ?? "";
        document.getElementById("cw-date").value = original.date;
        if (original.time) document.getElementById("cw-time").value = original.time;
        if (isScheduled) {
          const sched = wibParts(cw.scheduledTime);
          if (sched.date) {
            document.getElementById("cw-post").value = "schedule";
            document.getElementById("cw-sched-date").value = sched.date;
            document.getElementById("cw-sched-time").value = sched.time;
          }
          wireSchedule({
            now: "Saving publishes this assignment right away, and students are notified by Google Classroom.",
            schedule: "Google Classroom publishes this assignment and notifies students at the post time.",
          });
        }

        const input = document.getElementById("cw-files");
        document.getElementById("cw-add-files").addEventListener("click", () => input.click());
        input.addEventListener("change", () => {
          picked = [...picked, ...Array.from(input.files || [])];
          input.value = "";
          render();
        });
        const replaceInput = document.getElementById("cw-replace-input");
        replaceInput.addEventListener("change", () => {
          const file = replaceInput.files?.[0];
          if (file && replaceIndex !== null) replacements.set(replaceIndex, file);
          replaceInput.value = "";
          replaceIndex = null;
          render();
        });
        render();
      },
      didDestroy: () => previewUrls.forEach((url) => URL.revokeObjectURL(url)),
      preConfirm: async () => {
        const date = document.getElementById("cw-date").value;
        const time = document.getElementById("cw-time").value || "23:59";
        // Deadline yang tidak disentuh tidak dikirim, supaya tugas yang
        // deadline-nya sudah lewat tetap bisa diubah judul/instruksinya.
        const dueChanged = date !== original.date || (date && time !== original.time);
        const fields = readAssignmentForm({ checkDeadline: dueChanged });
        if (fields.error) return Swal.showValidationMessage(fields.error);
        const uploads = [...picked, ...replacements.values()];
        if (picked.length > MAX_FILES) return Swal.showValidationMessage(`At most ${MAX_FILES} new files can be attached.`);
        const tooBig = uploads.find((f) => f.size > MAX_FILE_BYTES);
        if (tooBig) return Swal.showValidationMessage(`File too large (max 15 MB): ${tooBig.name}`);
        const { title, description, maxPoints, dueAt, scheduledAt } = fields;
        try {
          // dueAt: tidak dikirim = tidak diubah, "" = deadline dihapus.
          // scheduledAt (hanya draft): "" = terbitkan sekarang.
          const body = new FormData();
          body.append("title", title);
          body.append("description", description);
          body.append("maxPoints", String(maxPoints));
          if (dueChanged) body.append("dueAt", dueAt || "");
          if (isScheduled) body.append("scheduledAt", scheduledAt || "");
          body.append("keep", JSON.stringify(kept.flatMap((k, i) => (k ? [i] : []))));
          replacements.forEach((file, i) => {
            if (kept[i]) body.append(`replace_${i}`, file);
          });
          picked.forEach((f) => body.append("files", f));
          const res = await axios.patch(`/classroom/coursework/${cw.id}`, body, { params: { courseId: cw.courseId } });
          return res.data;
        } catch (error) {
          return Swal.showValidationMessage(error.response?.data?.msg || "Failed to save the assignment.");
        }
      },
    });
    if (!result.isConfirmed || !result.value) return;
    swalSuccess(result.value.msg);
    const updated = { ...result.value.courseWork, courseId: cw.courseId };
    if (activeCourseWork?.id === cw.id) {
      // Tugas yang dibuat ulang punya id baru, jadi submission-nya dimuat ulang.
      if (updated.id !== cw.id) openSubmissions(updated);
      else {
        setActiveCourseWork(updated);
        setDueAt(dueAtOfCourseWork(updated));
      }
    }
    fetchCoursework();
  };

  // Hanya untuk tugas yang dibuat dari website (Google menolak yang lain).
  // Tidak bisa dibatalkan: submission student di Classroom ikut hilang.
  const handleDeleteAssignment = async (cw) => {
    const result = await Swal.fire({
      title: "Delete assignment?",
      html: `
        <div style="font-size:14px">
          <b id="cw-del-title"></b> will be deleted from Google Classroom.<br/><br/>
          All student submissions and grades for this assignment are deleted too, in Classroom and on this website.
          This cannot be undone.
        </div>`,
      icon: "warning",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      // Judul diisi lewat textContent: berasal dari input admin.
      didOpen: () => {
        document.getElementById("cw-del-title").textContent = `"${cw.title}"`;
      },
      preConfirm: async () => {
        try {
          const res = await axios.delete(`/classroom/coursework/${cw.id}`, { params: { courseId: cw.courseId } });
          return res.data;
        } catch (error) {
          return Swal.showValidationMessage(error.response?.data?.msg || "Failed to delete the assignment.");
        }
      },
    });
    if (!result.isConfirmed || !result.value) return;
    swalSuccess(result.value.msg);
    if (activeCourseWork?.id === cw.id) {
      setActiveCourseWork(null);
      setSubmissions([]);
      setDueAt(null);
      setStudentSearch("");
    }
    fetchCoursework();
  };

  // Satu course Classroom bisa punya lebih dari satu tugas. Yang dibuka adalah
  // tugas pertama, dan daftar Coursework tetap tampil di bawahnya supaya tugas
  // lain masih bisa dipilih — bukan disembunyikan hanya karena ada jalan pintas.
  const handleViewSubmissions = async (link) => {
    const isCurrent = link.classCode === classCode && String(link.batch) === String(batch);
    if (isCurrent) {
      // Kelas/batch tidak berubah, jadi efek di atas tidak akan jalan —
      // coursework-nya sudah ada di state, tinggal dibuka.
      if (coursework.length > 0) openSubmissions(coursework[0]);
      else swalError("No published coursework in this Classroom course.");
      return;
    }
    autoOpenRef.current = true;
    setClassCode(link.classCode);
    setBatch(String(link.batch));
  };

  // Membuka daftar tugas kelas itu (tempat Create/Edit/Delete), bukan langsung
  // submission tugas pertama seperti View Submissions. Daftarnya ada di bawah
  // tabel Linked Classes, jadi halaman digulir ke sana.
  const workAreaRef = useRef(null);
  const handleShowAssignments = (link) => {
    autoOpenRef.current = false;
    setActiveCourseWork(null);
    setSubmissions([]);
    setDueAt(null);
    setStudentSearch("");
    setClassCode(link.classCode);
    setBatch(String(link.batch));
    setTimeout(() => workAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const handleUnlink = async (link) => {
    const result = await Swal.fire({
      title: `Unlink ${link.classCode} batch ${link.batch}?`,
      html: `This only removes the link to <b>${link.courseName || link.courseId}</b>.<br/>The Classroom course, its assignments, and its grades are not touched.`,
      icon: "warning",
      showCancelButton: true,
      background: "white",
      color: "black",
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, unlink",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await axios.delete("/classroom/link", {
        params: { classCode: link.classCode, batch: link.batch },
      });
      // Baris nilai lokal sengaja tidak ikut terhapus di server — kalau ada
      // yang belum tersinkron, admin perlu tahu supaya tidak menganggap
      // nilainya sudah aman di Google.
      const { gradeRows, unsyncedRows } = res.data;
      let note = "";
      if (unsyncedRows > 0) {
        note = ` ${unsyncedRows} local grade(s) had never synced to Classroom and are still stored here.`;
      } else if (gradeRows > 0) {
        note = ` ${gradeRows} local grade row(s) kept.`;
      }
      swalSuccess(res.data.msg + note);

      // Kalau yang dilepas adalah kelas yang sedang dibuka, tabel di bawah
      // harus ikut dikosongkan — isinya sudah tidak punya tautan lagi.
      if (link.classCode === classCode && String(link.batch) === String(batch)) {
        setCoursework([]);
        setActiveCourseWork(null);
        setSubmissions([]);
        setDueAt(null);
      }
      fetchLinks();
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to unlink.");
    }
  };

  const openSubmissions = async (cw) => {
    setActiveCourseWork(cw);
    setStudentSearch("");
    setLoadingSubmissions(true);
    try {
      const res = await axios.get(`/classroom/coursework/${cw.id}/submissions`, { params: { courseId: cw.courseId } });
      setSubmissions(res.data.submissions || []);
      setDueAt(res.data.dueAt || null);
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to load submissions.");
      setSubmissions([]);
      setDueAt(null);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  // Pencarian dicocokkan ke email maupun nama student, keduanya case-insensitive,
  // supaya admin bisa mengetik potongan email ("yohana") atau nama lengkapnya.
  const filteredSubmissions = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return submissions;
    return submissions.filter((g) =>
      `${g.studentEmail || ""} ${g.studentName || ""}`.toLowerCase().includes(q),
    );
  }, [submissions, studentSearch]);

  // Semua lampiran dari submission yang sedang tampil diratakan jadi satu daftar
  // berurutan, supaya tombol Prev/Next di popup bisa berpindah antar file lintas
  // student — termasuk student yang mengumpulkan lebih dari satu file. Memakai
  // hasil filter, bukan seluruh submission, supaya navigasi popup tidak melompat
  // ke student yang tidak cocok dengan pencarian.
  const flatFiles = useMemo(() => {
    const out = [];
    filteredSubmissions.forEach((g) => {
      (g.attachments || []).forEach((a, attIndex) => {
        const n = normalizeAttachment(a);
        out.push({
          gradeId: g.id,
          attIndex,
          student: g.studentEmail || g.studentName || "unresolved",
          fileId: n.driveFileId,
          title: n.title,
          externalLink: n.externalLink,
          kind: n.kind,
        });
      });
    });
    return out;
  }, [filteredSubmissions]);

  const lateCount = useMemo(() => submissions.filter((g) => g.lateStatus === "late").length, [submissions]);

  const flatIndexOf = (gradeId, attIndex) =>
    flatFiles.findIndex((f) => f.gradeId === gradeId && f.attIndex === attIndex);

  // Menyimpan nilai TANPA memanggil openSubmissions() ulang: memuat ulang
  // seluruh daftar akan mereset flatFiles dan menutup popup di tengah penilaian.
  // Baris yang bersangkutan cukup ditimpa di state.
  const saveScore = async (grade) => {
    const raw = scoreInputs[grade.id] !== undefined ? scoreInputs[grade.id] : grade.score;
    if (raw === undefined || raw === null || raw === "") {
      swalError("Enter a score first.");
      return null;
    }
    try {
      const res = await axios.patch(`/classroom/submission/${grade.id}/grade`, { score: Number(raw) });
      setSubmissions((prev) =>
        prev.map((s) =>
          s.id === grade.id ? { ...s, ...res.data.grade, attachments: s.attachments, studentName: s.studentName } : s,
        ),
      );
      return res.data.msg;
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to save grade.");
      return null;
    }
  };

  const handleSaveScore = async (grade) => {
    const msg = await saveScore(grade);
    if (msg) swalSuccess(msg);
  };

  const closeViewer = useCallback(() => {
    setViewerIndex(null);
    setViewerMsg("");
  }, []);

  const stepViewer = useCallback(
    (delta) => {
      setViewerMsg("");
      setViewerIndex((i) => {
        if (i === null) return i;
        const next = i + delta;
        return next < 0 || next >= flatFiles.length ? i : next;
      });
    },
    [flatFiles.length],
  );

  // Panah kiri/kanan untuk pindah file, Esc untuk menutup. Diabaikan saat
  // fokus sedang di kolom nilai, supaya mengetik angka tidak ikut memindahkan file.
  useEffect(() => {
    if (viewerIndex === null) return undefined;
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "ArrowRight") stepViewer(1);
      else if (e.key === "ArrowLeft") stepViewer(-1);
      else if (e.key === "Escape") closeViewer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewerIndex, stepViewer, closeViewer]);

  // Kunci scroll halaman selama popup terbuka, supaya roda mouse hanya
  // menggerakkan isi popup. Lebar scrollbar yang hilang diganti jadi padding
  // body — tanpa itu konten di belakang melompat ke kanan saat popup dibuka.
  useEffect(() => {
    if (viewerIndex === null) return undefined;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
    // Sengaja hanya bergantung pada terbuka/tertutup, bukan pada viewerIndex:
    // berpindah file tidak perlu memasang ulang kuncinya.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerIndex === null]);

  // viewerIndex menunjuk posisi di flatFiles; begitu pencarian berubah daftar itu
  // disusun ulang sehingga indeks lama bisa menunjuk file student yang berbeda.
  useEffect(() => {
    closeViewer();
  }, [studentSearch, closeViewer]);

  const handleReturn = async (grade) => {
    try {
      await axios.post(`/classroom/submission/${grade.id}/return`);
      swalSuccess("Grade returned to student.");
      openSubmissions(activeCourseWork);
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to return grade.");
    }
  };

  const isLinked = links.some((l) => l.classCode === classCode && String(l.batch) === String(batch));
  // Google hanya mengizinkan nilai ditulis lewat API pada tugas yang dibuat
  // oleh project kita sendiri (Create assignment di halaman ini). Tugas yang
  // dibuat langsung di Classroom dinilai di sana; di sini nilainya hanya dibaca.
  const gradedHere = (cw) => cw.associatedWithDeveloper === true;
  const readOnly = Boolean(activeCourseWork) && !gradedHere(activeCourseWork);

  const viewerFile = viewerIndex !== null ? flatFiles[viewerIndex] : null;
  const viewerGrade = viewerFile ? submissions.find((s) => s.id === viewerFile.gradeId) : null;

  return (
    <div>
      <div className="adm-main">
        <PageHeader
          title="Assignment Grading"
          subtitle={
            <>
          Link an existing Google Classroom course to a class/batch. Assignments created here are graded here and written back to Classroom; assignments created in Classroom are graded there and their scores are read here.
            </>
          }
        />

        <div className="adm-card" style={{ padding: "20px 22px", marginBottom: "20px" }}>
          <p className="adm-card-title" style={{ marginBottom: "14px" }}>Link Classroom Course</p>
          <div className="row g-3 align-items-end">
            <div className="col-6 col-md-3">
              <label className="form-label">Class</label>
              <select className="form-select" value={classCode} onChange={(e) => setClassCode(e.target.value)}>
                {CLASS_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label">Batch</label>
              <input className="form-control" value={batch} onChange={(e) => setBatch(e.target.value.replace(/\D/g, ""))} placeholder="e.g. 4" />
            </div>
            <div className="col-6 col-md-4">
              <label className="form-label">Course</label>
              <select className="form-select" value={selectedCourseId} onChange={(e) => setSelectedCourseId(e.target.value)}>
                <option value="">Select course...</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <button className="adm-btn adm-btn--primary adm-btn--sm" disabled={linking} onClick={handleLink}>
                {linking ? "Linking..." : "Link"}
              </button>
            </div>
          </div>
          {courses.length === 0 && (
            <p style={{ marginTop: "10px", fontSize: "12px", color: "#c0392b" }}>
              No courses loaded — REFRESH_TOKEN likely needs to be regenerated with Classroom scope (see deploy notes).
            </p>
          )}
        </div>

        {links.length > 0 && (
          <div className="adm-card" style={{ padding: "20px 22px", marginBottom: "20px" }}>
            <p className="adm-card-title" style={{ marginBottom: "14px" }}>Linked Classes</p>
            <div style={{ overflowX: "auto" }}>
              <table className="table table-sm align-middle" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Batch</th>
                    <th>Classroom Course</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {links.map((l) => {
                    // Baris yang sedang dibuka ditandai supaya jelas coursework
                    // di bawahnya milik kelas/batch yang mana.
                    const isActive = l.classCode === classCode && String(l.batch) === String(batch);
                    return (
                      <tr key={`${l.classCode}-${l.batch}`} className={isActive ? "adm-row-active" : ""}>
                        <td style={{ fontSize: "13px" }}>
                          <span style={{ fontWeight: 600 }}>{l.classCode}</span>
                          <span style={{ color: "#888", fontSize: "12px" }}> — {l.className}</span>
                        </td>
                        <td style={{ fontSize: "13px" }}>{l.batch}</td>
                        <td style={{ fontSize: "13px", maxWidth: "320px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.courseName || l.courseId}>
                          {l.courseName || <span style={{ color: "#bbb" }}>(course id {l.courseId})</span>}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <div className="d-flex gap-1 justify-content-end">
                            <button
                              className="adm-btn adm-btn--primary adm-btn--sm"
                              onClick={() => handleShowAssignments(l)}
                            >
                              Assignments
                            </button>
                            <button
                              className="adm-btn adm-btn--secondary adm-btn--sm"
                              style={{ whiteSpace: "nowrap" }}
                              onClick={() => handleViewSubmissions(l)}
                            >
                              View Submissions
                            </button>
                            <button
                              className="adm-btn adm-btn--danger adm-btn--sm"
                              style={{ whiteSpace: "nowrap" }}
                              onClick={() => handleUnlink(l)}
                            >
                              Unlink
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Titik tujuan gulir tombol Assignments di tabel Linked Classes. */}
        <div ref={workAreaRef} style={{ scrollMarginTop: "16px" }} />

        {/* Daftar Coursework hanya tampil selama belum ada tugas yang dibuka —
            begitu tabel Submissions muncul, kartu ini cuma jadi perantara yang
            sudah dilewati. Jalan kembali ke daftar ini (dan ke tombol Create
            assignment) disediakan lewat tombol "All assignments" di header
            Submissions. */}
        {activeCourseWork ? null : loadingCoursework ? (
          <p style={{ color: "#888" }}>Loading coursework...</p>
        ) : coursework.length > 0 || isLinked ? (
          <div className="adm-card" style={{ padding: "20px 22px", marginBottom: "20px" }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: "10px" }}>
              <p className="adm-card-title" style={{ margin: 0 }}>Coursework — {classCode} batch {batch}</p>
              <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={handleCreateAssignment}>
                + Create assignment
              </button>
            </div>
            {coursework.length === 0 && (
              <p style={{ color: "#888", fontSize: "13px", margin: 0 }}>No published coursework in the linked course yet.</p>
            )}
            {coursework.map((cw) => (
              <div key={cw.id} className="d-flex justify-content-between align-items-center gap-2 mb-2 pb-2" style={{ borderBottom: "1px solid #f0f0f0" }}>
                <span style={{ fontSize: "13px" }}>
                  {cw.title}
                  <span
                    style={{
                      marginLeft: "8px",
                      padding: "1px 7px",
                      borderRadius: "10px",
                      fontSize: "11px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      color: gradedHere(cw) ? "#2e7d5b" : "#777",
                      backgroundColor: gradedHere(cw) ? "#e8f5f0" : "#f1f1f1",
                    }}
                  >
                    {gradedHere(cw) ? "Graded here" : "Graded in Classroom"}
                  </span>
                  {cw.state === "DRAFT" && (
                    <span
                      style={{
                        marginLeft: "6px",
                        padding: "1px 7px",
                        borderRadius: "10px",
                        fontSize: "11px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        color: "#8a6d1f",
                        backgroundColor: "#fdf6e3",
                      }}
                    >
                      {/* Google menerbitkan tugas terjadwal beberapa menit
                          setelah waktunya, bukan tepat pada menitnya. */}
                      {!cw.scheduledTime
                        ? "Draft"
                        : new Date(cw.scheduledTime).getTime() <= Date.now()
                          ? "Publishing — Google may take a few minutes"
                          : `Scheduled · ${fmtWIB(cw.scheduledTime)}`}
                    </span>
                  )}
                </span>
                <div className="d-flex gap-1">
                  <button className="adm-btn adm-btn--secondary adm-btn--sm" style={{ whiteSpace: "nowrap" }} onClick={() => openSubmissions(cw)}>
                    View Submissions
                  </button>
                  {gradedHere(cw) && (
                    <>
                      <button className="adm-btn adm-btn--secondary adm-btn--sm" style={{ whiteSpace: "nowrap" }} onClick={() => handleEditAssignment(cw)}>
                        Edit
                      </button>
                      <button className="adm-btn adm-btn--danger adm-btn--sm" style={{ whiteSpace: "nowrap" }} onClick={() => handleDeleteAssignment(cw)}>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          batch && <p style={{ color: "#888" }}>No Classroom course linked for {classCode} batch {batch} yet.</p>
        )}

        {activeCourseWork && (
          <div className="adm-card" style={{ padding: "20px 22px" }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: "10px" }}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {/* Selalu ada, juga untuk course bertugas tunggal: daftar
                    tugas adalah tempat tombol Create assignment berada. */}
                <button
                  type="button"
                  className="adm-btn adm-btn--secondary adm-btn--sm"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={() => {
                    setActiveCourseWork(null);
                    setSubmissions([]);
                    setDueAt(null);
                    setStudentSearch("");
                  }}
                >
                  &larr; All assignments
                </button>
                {gradedHere(activeCourseWork) && (
                  <>
                    <button className="adm-btn adm-btn--secondary adm-btn--sm" style={{ whiteSpace: "nowrap" }} onClick={() => handleEditAssignment(activeCourseWork)}>
                      Edit
                    </button>
                    <button className="adm-btn adm-btn--danger adm-btn--sm" style={{ whiteSpace: "nowrap" }} onClick={() => handleDeleteAssignment(activeCourseWork)}>
                      Delete
                    </button>
                  </>
                )}
                <div>
                  <p className="adm-card-title" style={{ margin: 0 }}>Submissions — {activeCourseWork.title}</p>
                  {submissions.length > 0 && (
                    <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#888" }}>
                      {dueAt ? `Deadline: ${fmtWIB(dueAt)}` : "Tugas ini tidak punya deadline di Classroom"}
                      {lateCount > 0 && <span style={{ color: "#c0392b" }}> · {lateCount} terlambat</span>}
                    </p>
                  )}
                </div>
              </div>
              <div style={{ position: "relative", width: "min(280px, 100%)" }}>
                <input
                  type="search"
                  className="form-control form-control-sm"
                  style={{ paddingRight: studentSearch ? "28px" : undefined }}
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  placeholder="Search email or name..."
                />
                {studentSearch && (
                  <button
                    type="button"
                    onClick={() => setStudentSearch("")}
                    title="Clear search"
                    style={{ position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, lineHeight: 1, fontSize: "16px", color: "#888", cursor: "pointer" }}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
            {readOnly && (
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ backgroundColor: "#fdf6e3", color: "#6b5516", borderRadius: "8px", padding: "10px 12px", marginBottom: "12px", fontSize: "13px" }}>
                <span style={{ flex: "1 1 320px" }}>
                  This assignment was created directly in Google Classroom, and Google only lets this website change grades on
                  assignments created here. Grade and return it in Classroom — the scores below are read from Classroom.
                </span>
                <div className="d-flex gap-1">
                  {activeCourseWork.alternateLink && (
                    <a className="adm-btn adm-btn--secondary adm-btn--sm" style={{ whiteSpace: "nowrap" }} href={activeCourseWork.alternateLink} target="_blank" rel="noreferrer">
                      Open in Classroom
                    </a>
                  )}
                  <button className="adm-btn adm-btn--secondary adm-btn--sm" style={{ whiteSpace: "nowrap" }} disabled={loadingSubmissions} onClick={() => openSubmissions(activeCourseWork)}>
                    Refresh grades
                  </button>
                </div>
              </div>
            )}
            {loadingSubmissions ? (
              <p style={{ color: "#888" }}>Loading...</p>
            ) : submissions.length === 0 ? (
              <p style={{ color: "#888" }}>No submissions yet.</p>
            ) : filteredSubmissions.length === 0 ? (
              <p style={{ color: "#888" }}>No student matches "{studentSearch}".</p>
            ) : (
              <div className="grading-scroll">
              <table className="table table-sm align-middle grading-freeze-table" style={{ minWidth: "1040px" }}>
                <thead>
                  <tr>
                    <th>Full Name</th>
                    <th>Student</th>
                    <th>File</th>
                    <th>State</th>
                    <th className="grading-freeze grading-freeze-turnedin">Turned In</th>
                    <th className="grading-freeze grading-freeze-score">Score</th>
                    <th className="grading-freeze grading-freeze-sync">Sync</th>
                    <th className="grading-freeze grading-freeze-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map((g) => (
                    <tr key={g.id}>
                      <td style={{ fontSize: "13px" }}>{g.studentName || <span style={{ color: "#bbb" }}>-</span>}</td>
                      <td style={{ fontSize: "13px" }}>{g.studentEmail || "unresolved (student sudah keluar dari course)"}</td>
                      <td style={{ fontSize: "12px", maxWidth: "260px" }}>
                        {(g.attachments || []).length === 0 ? (
                          <span style={{ color: "#bbb" }}>no file</span>
                        ) : (
                          (g.attachments || []).map((a, i) => {
                            const label = normalizeAttachment(a).title;
                            return (
                              <button
                                key={i}
                                type="button"
                                title={label}
                                onClick={() => setViewerIndex(flatIndexOf(g.id, i))}
                                style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: 0, color: "#0d9488", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
                              >
                                {label}
                              </button>
                            );
                          })
                        )}
                      </td>
                      <td style={{ fontSize: "12px" }}>{g.state}</td>
                      <td className="grading-freeze grading-freeze-turnedin" style={{ fontSize: "12px" }}>
                        {(() => {
                          const badge = LATE_BADGES[g.lateStatus] || LATE_BADGES.no_deadline;
                          const at = fmtWIB(g.turnedInAt);
                          return (
                            <>
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "1px 7px",
                                  borderRadius: "10px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  color: badge.color,
                                  backgroundColor: badge.background,
                                }}
                              >
                                {badge.label}
                              </span>
                              {at && <div style={{ fontSize: "11px", color: "#888", whiteSpace: "nowrap" }}>{at}</div>}
                            </>
                          );
                        })()}
                      </td>
                      <td className="grading-freeze grading-freeze-score">
                        {readOnly ? (
                          <span style={{ fontSize: "13px", fontWeight: 600 }}>{g.score ?? <span style={{ color: "#bbb" }}>-</span>}</span>
                        ) : (
                          <input
                            type="number"
                            className="form-control form-control-sm grading-score-input"
                            defaultValue={g.score ?? ""}
                            onChange={(e) => setScoreInputs((prev) => ({ ...prev, [g.id]: e.target.value }))}
                          />
                        )}
                      </td>
                      {/* Di tugas read-only, sync_failed adalah nilai yang pernah
                          disimpan di sini dan ditolak Google: belum ada di Classroom. */}
                      <td className="grading-freeze grading-freeze-sync" style={{ fontSize: "12px", color: g.syncStatus === "sync_failed" ? "#c0392b" : "#2e9e6b" }}>
                        {readOnly && g.syncStatus === "sync_failed" ? "not in Classroom" : g.syncStatus}
                      </td>
                      <td className="grading-freeze grading-freeze-actions" style={{ whiteSpace: "nowrap" }}>
                        {/* flex HARUS di div, bukan di td: Bootstrap .d-flex menimpa
                            display:table-cell sehingga sel keluar dari layout tabel
                            dan tombolnya tidak lagi sejajar dengan barisnya. */}
                        {!readOnly && (
                          <div className="d-flex gap-1">
                            <button className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => handleSaveScore(g)}>Save</button>
                            <button className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => handleReturn(g)}>Return</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}
      </div>

      {viewerFile && (
        <div
          onClick={closeViewer}
          className="adm-modal-backdrop" style={{ zIndex: 1050, padding: "24px", overscrollBehavior: "contain" }}
        >
          {/* stopPropagation supaya klik di dalam panel tidak menutup popup */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="adm-modal" style={{ width: "min(1100px, 95vw)", height: "90vh", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
          >
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {viewerFile.title}
                </div>
                <div style={{ fontSize: "12px", color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {viewerFile.student} · file {viewerIndex + 1} dari {flatFiles.length}
                </div>
              </div>
              {viewerFile.externalLink && (
                <a href={viewerFile.externalLink} target="_blank" rel="noreferrer" style={{ fontSize: "12px", color: "#0d9488" }}>
                  Buka di Drive
                </a>
              )}
              <button type="button" onClick={closeViewer} style={{ background: "none", border: "none", fontSize: "22px", lineHeight: 1, cursor: "pointer", color: "#888" }}>
                &times;
              </button>
            </div>

            <div style={{ flex: 1, backgroundColor: "#f7f7f7", position: "relative" }}>
              {viewerFile.fileId ? (
                // /preview adalah endpoint Drive yang memang untuk disematkan, dan
                // merender .docx, Google Docs, PDF, maupun gambar dengan cara yang sama.
                <iframe
                  key={viewerFile.fileId}
                  title={viewerFile.title}
                  src={`https://drive.google.com/file/d/${viewerFile.fileId}/preview`}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              ) : (
                <div style={{ padding: "24px", color: "#888", fontSize: "13px" }}>
                  {viewerFile.externalLink ? (
                    <>
                      Lampiran ini bukan file Google Drive
                      {viewerFile.kind === "link"
                        ? " (student mengumpulkan tautan)"
                        : viewerFile.kind === "youtube"
                          ? " (video YouTube)"
                          : viewerFile.kind === "form"
                            ? " (Google Form)"
                            : ""}
                      , jadi tidak bisa ditampilkan langsung di sini.
                      <div style={{ marginTop: "10px" }}>
                        <a href={viewerFile.externalLink} target="_blank" rel="noreferrer" style={{ color: "#0d9488" }}>
                          Buka tautannya di tab baru
                        </a>
                      </div>
                    </>
                  ) : (
                    "File ini tidak bisa ditampilkan — kemungkinan sudah dihapus student."
                  )}
                </div>
              )}
            </div>

            <div style={{ padding: "12px 16px", borderTop: "1px solid #eee", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" disabled={viewerIndex === 0} onClick={() => stepViewer(-1)}>
                &larr; Prev
              </button>
              <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" disabled={viewerIndex === flatFiles.length - 1} onClick={() => stepViewer(1)}>
                Next &rarr;
              </button>

              <div style={{ flex: 1 }} />

              {viewerGrade && readOnly && (
                <span style={{ fontSize: "12px", color: "#888" }}>
                  Score <b style={{ color: "#333" }}>{viewerGrade.score ?? "-"}</b> · graded in Classroom
                </span>
              )}
              {viewerGrade && !readOnly && (
                <>
                  <span style={{ fontSize: "12px", color: "#888" }}>Score</span>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: "90px" }}
                    value={scoreInputs[viewerGrade.id] !== undefined ? scoreInputs[viewerGrade.id] : viewerGrade.score ?? ""}
                    onChange={(e) => setScoreInputs((prev) => ({ ...prev, [viewerGrade.id]: e.target.value }))}
                  />
                  <span style={{ fontSize: "13px", fontWeight: 600, width: "18px" }}>{viewerGrade.letter || "-"}</span>
                  <button
                    type="button"
                    className="adm-btn adm-btn--primary adm-btn--sm"
                    onClick={async () => {
                      const msg = await saveScore(viewerGrade);
                      if (msg) setViewerMsg(msg);
                    }}
                  >
                    Save
                  </button>
                  {viewerMsg && <span style={{ fontSize: "12px", color: "#2e9e6b" }}>{viewerMsg}</span>}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentGradingPage;
